import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Player,
  PlayerDetail,
  PlayerActivity,
  PlayerStats,
  PlayerFilters,
  CohortAnalysis,
  CohortData,
  CommissionBreakdownItem,
  AccountFacet,
} from '../types/players.types';

// API Configuration
const API_BASE_URL = '/api';

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch {
    return null;
  }
  return null;
};

/**
 * Create headers with authentication
 */
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/**
 * Handle API response
 */
const handleAPIResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData: any = await response.json().catch(() => ({
      error: 'Request failed',
      message: response.statusText,
    }));
    throw new Error((errorData as any)?.error || (errorData as any)?.message || 'API request failed');
  }

  const data: any = await response.json();
  return data.data || data;
};

// Hooks
export const usePlayerStats = () => {
  return useQuery({
    queryKey: ['player-stats'],
    queryFn: async (): Promise<PlayerStats> => {
      const response = await fetch(`${API_BASE_URL}/players/stats`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(errorData.error || 'Failed to fetch player stats');
      }
      
      const result = await response.json() as { success: boolean; data?: any };
      // API returns { success, data: { totalPlayers, activePlayers, ... } }
      const data = result.data || result;
      
      const eur = (n: unknown) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(Number(n) || 0);

      return {
        totalPlayers: data.totalPlayers || 0,
        activePlayers: data.activePlayers || 0,
        newPlayersToday: data.newPlayersThisMonth || 0,
        totalLTV: eur(data.totalLTV),
        currency: 'EUR',
        averageLTV: eur(data.averages?.ltpPerPlayer),
        topCommissionTier: 'tier1',
      };
    },
    staleTime: 30000,
  });
};

/**
 * Build the query string for /api/players from the filter state
 */
const buildPlayersQuery = (filters: PlayerFilters, page: number, perPage: number): URLSearchParams => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: perPage.toString(),
    ...(filters.search && { search: filters.search }),
    ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
    ...(filters.dateTo && { dateTo: filters.dateTo }),
    ...(filters.minLTV !== undefined && { minLTV: filters.minLTV.toString() }),
    ...(filters.maxLTV !== undefined && { maxLTV: filters.maxLTV.toString() }),
    ...(filters.dealType && { dealType: filters.dealType }),
    ...(filters.ftdOnly && { ftd: '1' }),
    ...(filters.withCommission && { withCommission: '1' }),
    sortBy: filters.sortBy || 'registration_date',
    sortOrder: filters.sortOrder || 'desc',
  });

  if (filters.networkIds?.length > 0) {
    filters.networkIds.forEach(id => params.append('network', id));
  }
  if (filters.status?.length > 0) {
    // API supports a single status value
    params.set('status', filters.status[0]);
  }
  return params;
};

interface PlayersPage {
  players: Player[];
  total: number;
  page: number;
  pageSize: number;
}

const fetchPlayersPage = async (filters: PlayerFilters, page: number, perPage: number): Promise<PlayersPage> => {
  const params = buildPlayersQuery(filters, page, perPage);
  const response = await fetch(`${API_BASE_URL}/players?${params.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(errorData.error || 'Failed to fetch players');
  }

  const result = await response.json() as { success: boolean; data?: Player[]; total?: number; page?: number; pageSize?: number };
  return {
    players: result.data || [],
    total: result.total || 0,
    page: result.page || page,
    pageSize: result.pageSize || perPage,
  };
};

export const usePlayers = (filters: PlayerFilters, page = 1, perPage = 25) => {
  return useQuery({
    queryKey: ['players', filters, page, perPage],
    queryFn: () => fetchPlayersPage(filters, page, perPage),
    staleTime: 30000,
    placeholderData: (previous) => previous,
  });
};

/**
 * Accounts that have players (with brand / program / platform labels and counts)
 */
export const usePlayerFacets = () => {
  return useQuery({
    queryKey: ['player-facets'],
    queryFn: async (): Promise<AccountFacet[]> => {
      const response = await fetch(`${API_BASE_URL}/players/facets`, { headers: getAuthHeaders() });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(errorData.error || 'Failed to fetch player facets');
      }
      const result = await response.json() as { success: boolean; data?: { accounts?: AccountFacet[] } };
      return result.data?.accounts || [];
    },
    staleTime: 60000,
  });
};

/**
 * Fetch every player matching the filters (for CSV export), capped at `maxRows`.
 */
export const fetchAllPlayers = async (filters: PlayerFilters, maxRows = 5000): Promise<Player[]> => {
  const pageSize = 100;
  const all: Player[] = [];
  let page = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const result = await fetchPlayersPage(filters, page, pageSize);
    all.push(...result.players);
    if (result.players.length < pageSize || all.length >= result.total || all.length >= maxRows) break;
    page++;
  }
  return all.slice(0, maxRows);
};

export const usePlayerDetail = (id: string) => {
  return useQuery({
    queryKey: ['player-detail', id],
    queryFn: async (): Promise<PlayerDetail> => {
      const response = await fetch(`${API_BASE_URL}/players/${id}`, {
        headers: getAuthHeaders(),
      });
      return handleAPIResponse<PlayerDetail>(response);
    },
    staleTime: 30000,
    enabled: !!id,
  });
};

export const usePlayerActivities = (id: string) => {
  return useQuery({
    queryKey: ['player-activities', id],
    queryFn: async (): Promise<PlayerActivity[]> => {
      const response = await fetch(`${API_BASE_URL}/players/${id}/activity`, {
        headers: getAuthHeaders(),
      });
      return handleAPIResponse<PlayerActivity[]>(response);
    },
    staleTime: 30000,
    enabled: !!id,
  });
};

export const useCommissionBreakdown = (id: string) => {
  return useQuery({
    queryKey: ['commission-breakdown', id],
    queryFn: async (): Promise<CommissionBreakdownItem[]> => {
      const response = await fetch(`${API_BASE_URL}/players/${id}/commissions`, {
        headers: getAuthHeaders(),
      });
      return handleAPIResponse<CommissionBreakdownItem[]>(response);
    },
    staleTime: 30000,
    enabled: !!id,
  });
};

export const useCohortAnalysis = (dateRange?: { from: string; to: string }) => {
  return useQuery({
    queryKey: ['cohort-analysis', dateRange],
    queryFn: async (): Promise<CohortAnalysis> => {
      const params = new URLSearchParams();
      if (dateRange?.from) params.append('from', dateRange.from);
      if (dateRange?.to) params.append('to', dateRange.to);

      const response = await fetch(`${API_BASE_URL}/players/cohorts?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      return handleAPIResponse<CohortAnalysis>(response);
    },
    staleTime: 60000,
  });
};

export const useUpdatePlayerNotes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const response = await fetch(`${API_BASE_URL}/players/${id}/notes`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes }),
      });
      return handleAPIResponse<void>(response);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['player-detail', id] });
      toast.success('Notes updated successfully');
    },
    onError: () => toast.error('Failed to update notes'),
  });
};
